import { useState, useCallback, useEffect } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { MMS_CONSTANTS } from '../utils/sms-constants';
import { useToast } from '@/hooks/use-toast';

interface GiphySearchProps {
  onGifSelected: (file: File, originalUrl: string) => void;
}

interface GiphyGif {
  id: string;
  url: string;
  preview: string;
  size: number;
}

interface GiphyResponse {
  data: {
    id: string;
    images: {
      downsized?: {
        url: string;
      };
      fixed_width?: {
        url: string;
      };
      fixed_height: {
        url: string;
      };
    };
  }[];
}

export function GiphySearch({ onGifSelected }: GiphySearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [gifs, setGifs] = useState<GiphyGif[]>([]);
  const { toast } = useToast();

  const fetchGifs = useCallback(
    async (endpoint: string, params: Record<string, string>) => {
      setIsLoading(true);
      try {
        const queryParams = new URLSearchParams({
          api_key: import.meta.env.VITE_GIPHY_API_KEY,
          limit: '50', // Increased limit to ensure we get enough valid GIFs after filtering
          rating: 'pg',
          ...params
        });

        const response = await fetch(`https://api.giphy.com/v1/gifs/${endpoint}?${queryParams}`);
        const data = (await response.json()) as GiphyResponse;

        // Process and filter GIFs
        const processedGifs = await Promise.all(
          data.data.map(async (gif) => {
            // Use downsized version if available, fallback to fixed_width
            const gifUrl = gif.images.downsized?.url ?? gif.images.fixed_width?.url;
            if (!gifUrl) return null;

            try {
              // Check actual file size
              const response = await fetch(gifUrl, { method: 'HEAD' });
              const size = parseInt(response.headers.get('content-length') ?? '0', 10);

              return {
                id: gif.id,
                url: gifUrl,
                preview: gif.images.fixed_height.url,
                size
              };
            } catch {
              return null;
            }
          })
        );

        // Filter out null values and GIFs that are too large, and take first 8
        const validGifs = processedGifs
          .filter((gif): gif is GiphyGif => gif !== null && gif.size <= MMS_CONSTANTS.MAX_FILE_SIZE)
          .slice(0, 8);

        setGifs(validGifs);
      } catch (error) {
        console.error('Error fetching GIFs:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch GIFs. Please try again.',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    },
    [toast]
  );

  const searchGifs = useCallback(() => {
    if (!searchTerm.trim()) return;
    void fetchGifs('search', { q: searchTerm.trim() });
  }, [searchTerm, fetchGifs]);

  const loadTrendingGifs = useCallback(() => {
    void fetchGifs('trending', {});
  }, [fetchGifs]);

  // Load trending GIFs on mount
  useEffect(() => {
    loadTrendingGifs();
  }, [loadTrendingGifs]);

  const handleGifSelect = useCallback(
    async (gif: GiphyGif) => {
      try {
        // Download the GIF
        const response = await fetch(gif.url);
        const blob = await response.blob();

        // Create a File object
        const file = new File([blob], `giphy-${Date.now()}.gif`, { type: 'image/gif' });
        onGifSelected(file, gif.url);
      } catch (error) {
        console.error('Error processing GIF:', error);
        toast({
          title: 'Error',
          description: 'Failed to process GIF. Please try again.',
          variant: 'destructive'
        });
      }
    },
    [onGifSelected, toast]
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input
          type="text"
          placeholder="Search GIPHY..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              searchGifs();
            }
          }}
        />
        <Button
          onClick={() => {
            searchGifs();
          }}
          disabled={isLoading}
        >
          <Search className="h-4 w-4" />
        </Button>
      </div>

      {gifs.length === 0 && !isLoading && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">
            No GIFs found under {MMS_CONSTANTS.MAX_FILE_SIZE_DISPLAY}
          </p>
          <Button
            variant="link"
            onClick={() => {
              loadTrendingGifs();
            }}
            className="mt-2"
          >
            Load trending GIFs
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {gifs.map((gif) => (
          <button
            key={gif.id}
            className="relative aspect-square overflow-hidden rounded-lg hover:ring-2 hover:ring-primary transition-all"
            onClick={() => {
              void handleGifSelect(gif);
            }}
          >
            <img src={gif.preview} alt="GIF" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="text-center">
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      )}
    </div>
  );
}
